import * as moment from 'moment';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { BehaviorSubject, EMPTY, Observable, of, Subscription } from 'rxjs';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { Task } from '@shared/models/task.model';
import { AttachmentManagerListComponent } from '@shared/modules/attachment-manage/components/attachment-manager-list/attachment-manager-list.component';
import { MatDialog } from '@angular/material/dialog';
import { ObjectTaskType, TaskType } from '@shared/enums/task-type.enum';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EventEmitter, Injectable } from '@angular/core';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { Config } from '@shared/configs/config';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { TASK_SIDENAV_FORM } from '@shared/modules/task-sidenav/const/task-sidenav-form';
import { TaskViewState } from '@shared/enums/task-view-state.enum';
import {
  concatMap,
  distinctUntilChanged,
  finalize,
  tap,
  filter,
  switchMap,
  take,
  mapTo
} from 'rxjs/operators';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectChangesStatus } from '@modules/projects/shared/enums/project-changes-status.enum';
import { ChangeSource } from '@modules/projects/shared/enums/change-source.enum';
import { ETaskAction } from '../enums/task-action.enum';
import { ITaskActionEvent } from '../interfaces/task-action-event.interface';
import { StorageService } from '@core/services/storage.service';
import { TaskPermissionController } from '@shared/controllers/task-permission.controller';
import { AssignedContactList } from '@shared/models/assigned-contact.model';
import { ProjectChangesSettlementStatus } from '@modules/projects/shared/enums/project-change-settlement-status.enum';
import { TaskSelectionV2ChangeComponent } from '@shared/modules/tasks/components/task-selection-change-v2/task-selection-change-v2.component';
import { EmployeeSearchComponent } from '@shared/components/employee-search/employee-search.component';
import { SubtasksService } from '@shared/modules/subtasks/subtasks.service';
import { FinancialList } from '@shared/consts/financial.list';
import { DaysList } from '@shared/consts/days.list';
import { TaskTypeList } from '@shared/consts/task-type.list';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { SnackBarService } from '@core/services/snackbar.service';

interface ActivityTemplateContext {
  stage: ProjectStage;
  sortOrder?: number | null;
  parentId?: number | null;
}

@Injectable({ providedIn: 'root' })
export class TaskSidenavService {
  TASK_SIDENAV_FORM = TASK_SIDENAV_FORM;
  TaskConfigType: TaskType = TaskType.TYPE_TASK;
  form: UntypedFormGroup;
  loading: boolean = false;
  projectID: number = null;
  defaultGroupID: number = null;
  nextTaskCreation: boolean = false;
  showInCalendar: boolean = false;
  groupType: 'project' | 'without_project' | 'activity-template' = null;
  attachmentsListRef: AttachmentManagerListComponent;
  searchEmployeeRef: EmployeeSearchComponent;
  groupRef: any;
  financeTemplate: TaskSelectionV2ChangeComponent;
  daysTemplate: TaskSelectionV2ChangeComponent;
  formSubmitted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  isOpened: boolean = false;
  taskId: number = null;
  task: Task = null;
  connectedTask: Task = null;
  viewState: TaskViewState = null;
  loadingTask: boolean = false;
  safeContent: any = null;
  safeContentInternalNote: any = null;

  mockList: AssignedContactList = new AssignedContactList();

  subUpdateTask: Subscription = new Subscription();

  assignedUsers: any = [];
  permissionCtrl: TaskPermissionController = new TaskPermissionController();

  financial: Array<any> = FinancialList.map((i) => {
    i.label = this.t.instant(i.label);
    return i;
  });

  days: Array<any> = DaysList.map((i) => {
    i.label = this.t.instant(i.label);
    return i;
  });

  selectedType: { type: string; name: string } = TaskTypeList[0];
  selectedGroup: { id: number; text: string } = null;
  selectedDate: { start: string; end: string } = null;
  selectedEmployees: { assignedToIds?: number[]; assignedEmails?: string[] } = null;
  activityTemplateContext: ActivityTemplateContext | null = null;
  activityTemplateTaskType: string | null = null;
  isProcessTaskContext: boolean = false;

  get hasProcessTaskContext(): boolean {
    return !!this.isProcessTaskContext || this.groupType === 'activity-template' || !!this.activityTemplateContext;
  }

  previewFormSub: Subscription;
  statusSubscription: Subscription;
  taskSubscription: Subscription;
  sub: Subscription = new Subscription();

  savedFormValues: Record<TASK_SIDENAV_FORM, string>;
  taskActionEmitter: EventEmitter<ITaskActionEvent> = new EventEmitter();

  changeStatusHelper: UntypedFormControl = new UntypedFormControl(ProjectChangesStatus.CHANGE_STATUS_CREATED);

  get canEditTask() {
    return this.permissionCtrl.edit && (this.isPreview || this.isEdit);
  }

  get canAddTask() {
    return this.permissionCtrl.add && this.isCreation;
  }

  get isTaskSelection() {
    return this.isTask || this.isCorrection || this.isOrder || this.isNote || this.isClientTask;
  }

  get isTaskCompanyClient() {
    return this.isCompanyTask || this.isClientTask;
  }

  get isCompanyTask() {
    return this.TaskConfigType === TaskType.TYPE_COMPANY_TASK || this.TaskConfigType === TaskType.TYPE_TASK;
  }

  get FormType() {
    return this.form.get(TASK_SIDENAV_FORM.type).value;
  }

  get isChange() {
    return this.FormType === TaskType.TYPE_CHANGE;
  }

  get isConfigTask() {
    return this.FormType !== TaskType.TYPE_CHANGE;
  }

  get isClientTask() {
    return this.FormType === TaskType.TYPE_CLIENT_TASK;
  }

  get isNote() {
    return this.FormType === TaskType.TYPE_NOTE;
  }

  get isOrder() {
    return this.FormType === TaskType.TYPE_ORDER;
  }

  get isConfigChange() {
    return this.FormType === TaskType.TYPE_CHANGE;
  }

  get isCorrection() {
    return this.FormType === TaskType.TYPE_CORRECTION;
  }

  get isTask() {
    return this.FormType === TaskType.TYPE_TASK;
  }

  get isProtocol() {
    return this.form.get(TASK_SIDENAV_FORM.source).value === ChangeSource.SOURCE_PROTOCOL;
  }

  get formProjectId() {
    return this.form.get(TASK_SIDENAV_FORM.projectId).value;
  }

  get TagUrl() {
    const id = !isNaN(Number(this.formProjectId)) ? Number(this.formProjectId) : null;
    return `${Config.API}/tag/type?type=task-tag${id ? '&projectId=' + id : ''}`;
  }

  get objectType() {
    return this.form.get(TASK_SIDENAV_FORM.objectType).value;
  }

  get initialFormValues() {
    return {
      [TASK_SIDENAV_FORM.status]: TaskStatus.CREATED,
      [TASK_SIDENAV_FORM.changeStatus]: ProjectChangesStatus.CHANGE_STATUS_CREATED,
      [TASK_SIDENAV_FORM.objectType]: ObjectTaskType.PLAIN,
      [TASK_SIDENAV_FORM.type]: this.TaskConfigType,
      [TASK_SIDENAV_FORM.id]: null, // Helper ID is used to fill project/protocol id before send
      [TASK_SIDENAV_FORM.groupId]: this.defaultGroupID,
      [TASK_SIDENAV_FORM.isHighPriority]: false,
      [TASK_SIDENAV_FORM.hiddenForClient]: true,
      [TASK_SIDENAV_FORM.discount]: 0,
      [TASK_SIDENAV_FORM.description]: '',
      [TASK_SIDENAV_FORM.content]: null,
      [TASK_SIDENAV_FORM.termStart]: null,
      [TASK_SIDENAV_FORM.termEnd]: null,
      [TASK_SIDENAV_FORM.assignedTo]: null,
      [TASK_SIDENAV_FORM.tags]: null,
      [TASK_SIDENAV_FORM.attachments]: [],
      [TASK_SIDENAV_FORM.attachmentPacketId]: null,
      [TASK_SIDENAV_FORM.price]: 0,
      [TASK_SIDENAV_FORM.isAdditionalTimeEstimated]: 0,
      [TASK_SIDENAV_FORM.isPriceEstimated]: 0,
      [TASK_SIDENAV_FORM.additionalTime]: 0,
      [TASK_SIDENAV_FORM.source]: null,
      [TASK_SIDENAV_FORM.linkedChangeId]: null,
      [TASK_SIDENAV_FORM.projectId]: this.projectID,
      [TASK_SIDENAV_FORM.protocolId]: null,
      [TASK_SIDENAV_FORM.internalNote]: '',
      [TASK_SIDENAV_FORM.settlementStatus]: ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_NONE,
      [TASK_SIDENAV_FORM.showInCalendar]: this.showInCalendar,
      [TASK_SIDENAV_FORM.changeType]: null
    };
  }

  get isPreview() {
    return this.viewState === TaskViewState.PREVIEW;
  }

  get isEdit() {
    return this.viewState === TaskViewState.EDIT;
  }

  get isCreation() {
    return this.viewState === TaskViewState.CREATE;
  }

  get currentFormValues() {
    return Object.assign(this.form.value, {});
  }

  get isStatusChanged() {
    return this.savedFormValues?.changeStatus !== this.currentFormValues?.changeStatus;
  }

  get isFormValid() {
    if (this.isConfigChange) {
      return this.form.valid && this.financeTemplate.form.valid && this.daysTemplate.form.valid;
    }
    return this.form.valid;
  }

  constructor(
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    public sideService: SidenavService,
    private taskService: TaskService,
    public listTaskService: ListTaskService,
    public t: TranslateService,
    private sanitizer: DomSanitizer,
    public storage: StorageService,
    private subtasksService: SubtasksService,
    private snackBarService: SnackBarService
  ) {
    this.createForm();
  }

  setInitialData(options: any) {
    this.changeStatusHelper.setValue(ProjectChangesStatus.CHANGE_STATUS_CREATED);
    this.isOpened = true;
    this.viewState = options?.viewState || null;

    this.setProjectId(Number(options?.projectID));
    this.setObjectType(options?.extra?.objectType);
    this.setTaskTypeOnInit(options?.extra?.type);
    this.setActivityTemplateContext(options?.extra?.activityTemplateContext || null);
    this.setActivityTemplateTaskType(
      options?.extra?.activityTemplateTaskType || options?.extra?.type || null
    );
    this.isProcessTaskContext =
      !!options?.extra?.isProcessTaskContext || !!options?.extra?.activityTemplateContext?.stage;
    this.setGroupID(options.groupId || options.taskGroupId);
    this.setAssigneeEmails(options?.assignToEmails);
    this.setAssigneeIds(options?.assignToIds);
    this.setVisibilityInCalendar(options?.showInCalendar || false);
    this.setGroupType(options?.groupType || false);

    //#endregion

    //#region set form data from server
    this.isCreation ? this.requestConnectedTask(options?.connectedTaskId) : null;
    this.isPreview || this.isEdit ? this.setPreviewState(options) : null;
    //#endregion

    //#region set validators
    this.setRequiredValidators(TASK_SIDENAV_FORM.projectId, !!this.isConfigTask);
    //#endregion

    //#region set subscribers
    this.sourceSubscriber();
    setTimeout(() => {
      this.typeSubscriber();
    }, 200);

    //#endregion

    //#region set permission
    this.permissionCtrl.initPermissions(this.task);
    //#endregion

    //#region set historical data
    this.setFromHistory();
    //#endregion
  }

  setVisibilityInCalendar(visible: boolean) {
    this.showInCalendar = visible;
    this.form.get(TASK_SIDENAV_FORM.showInCalendar).setValue(this.showInCalendar);
  }

  getTaskRequest(id: number): Observable<Task> {
    this.loadingTask = true;
    return this.taskService.getTask(id);
  }

  setProjectId(id: number) {
    if (!id) return;

    this.projectID = id || null;
    this.form.get(TASK_SIDENAV_FORM.projectId).setValue(id);
  }

  setObjectType(type: TaskType) {
    if (!type) {
      return;
    }
    this.form.get(TASK_SIDENAV_FORM.objectType).setValue(type);
  }

  getConnectedTask(connectedTaskId: number) {
    return this.getTaskRequest(connectedTaskId).pipe(
      tap((task: Task) => this.successSettingConnectedTask(task))
    );
  }

  requestConnectedTask(connectedTaskId: number) {
    if (!connectedTaskId) {
      return;
    }
    this.getConnectedTask(connectedTaskId)
      .subscribe()
      .add(() => {
        this.loadingTask = false;
      });
  }

  successSettingConnectedTask(task: Task) {
    this.setConnectedTask(task);
    this.form.get(TASK_SIDENAV_FORM.description).setValue(task.description);
    this.setFromHistory();
  }

  setConnectedTask(task: Task) {
    this.connectedTask = task ? this.createTask(task) : null;
    this.form.get(TASK_SIDENAV_FORM.linkedChangeId).setValue(task?.id || null);
  }

  setFromHistory() {
    this.savedFormValues = this.currentFormValues;
  }

  sourceSubscriber() {
    const sourceSub = this.form
      .get(TASK_SIDENAV_FORM.source)
      .valueChanges.subscribe(() =>
        this.setRequiredValidators(TASK_SIDENAV_FORM.protocolId, !this.isProtocol)
      );

    this.sub.add(sourceSub);
  }

  typeSubscriber() {
    const typeSub = this.form.get(TASK_SIDENAV_FORM.type).valueChanges.subscribe((type: string) => {
      this.updateTaskType(type);
    });
    this.sub.add(typeSub);
  }

  updateTaskType(type: string) {
    const index = this.groupRef?._items?.findIndex((i) => i.name === type);
    this.setRequiredValidators(TASK_SIDENAV_FORM.projectId, !!this.isConfigTask);

    this.form.get(TASK_SIDENAV_FORM.groupId).setValue(null);

    if (index > -1) {
      this.defaultGroupID = this.groupRef._items[index].id;
      this.form.get(TASK_SIDENAV_FORM.groupId).patchValue(this.groupRef._items[index].id, { onlySelf: true });
    }

    if (!this.isConfigChange) {
      this.form.get(TASK_SIDENAV_FORM.source).setValue(null);
    }
    this.taskActionEmitter.emit({ action: ETaskAction.ACTION_UPDATE_TEMPLATE });
  }

  compareFormValues(
    stateOld: Record<TASK_SIDENAV_FORM, string>,
    stateNew: Record<TASK_SIDENAV_FORM, string>
  ) {
    return JSON.stringify(stateOld) === JSON.stringify(stateNew);
  }

  getSidenavTask(options) {
    const source = options.task ? of(options.task) : this.getTaskRequest(options?.taskId);
    return source.pipe(tap((task) => this.initPreviewState(task)));
  }

  setPreviewState(options: any) {
    this.loadingTask = true;
    this.getSidenavTask(options)
      .subscribe()
      .add(() => {
        this.loadingTask = false;
      });
  }

  setViewTerm() {
    this.rangeChanged({ start: this.task?.termStart, end: this.task?.termEnd });
  }

  setTaskTypeFromSave() {
    const control = this.form.get(TASK_SIDENAV_FORM.type);
    this.selectedType = TaskTypeList.filter((i) => i.type === control.value)[0] || null;
  }

  setViewTags() {
    this.form.get(TASK_SIDENAV_FORM.tags).setValue(this.task.tags.map((i) => i.name) || null);
  }

  setViewAssignedEmployees() {
    const ids = this.task?.assignedTo
      .map((i) => i.id)
      .concat((this.task?.assignedEmails?.map((i: string) => i) as any) || []);
    this.form.get(TASK_SIDENAV_FORM.assignedTo).setValue(ids);
  }

  setStatusCompleted(task: Task) {
    this.task.status = task.status;
    this.listTaskService.emitter.emit({ type: ListTaskEventType.UPDATE_TASK, data: { task: this.task } });
    this.form.get(TASK_SIDENAV_FORM.status).setValue(this.task.status);
  }

  initPreviewState(task: Task) {
    this.taskActionEmitter.emit({ action: ETaskAction.ACTION_TASK_LOADED });
    this.sub?.unsubscribe();
    this.taskSubscription?.unsubscribe();
    this.subUpdateTask?.unsubscribe();
    this.previewFormSub?.unsubscribe();

    this.sub = new Subscription();
    this.setTaskTypeOnInit(task.type);
    // 1. Fill the form from backend
    this.fillPreviewState(task);
    // 2.  Set connected Task
    this.setConnectedTask(task?.linkedChange);
    // 3. Subscribe for form changes without first change
    // 4. Save Form on any submit
    this.subscribePreviewChange();
    this.subscribeUpdateTask();

    this.setFromHistory();
  }

  setTaskTypeOnInit(type: TaskType) {
    if (!type) return;

    this.TaskConfigType = type;
    this.form.get(TASK_SIDENAV_FORM.type).setValue(this.TaskConfigType);
  }

  setActivityTemplateContext(context: ActivityTemplateContext | null) {
    this.activityTemplateContext = context?.stage ? context : null;
  }

  setActivityTemplateTaskType(type: string | null) {
    this.activityTemplateTaskType = type || null;
  }

  fillPreviewState(task: Task) {
    this.setNewTask(task);
    if (!this.defaultGroupID) {
      this.setTaskGroup({
        id: task?.taskGroup?.id,
        text: this.translate('Projects.TaskGroups.', task?.taskGroup?.name)
      });
    }
    this.defaultFormFill();
    this.setViewTerm();
    this.setTaskTypeFromSave();
    this.setViewTags();
    this.setViewAssignedEmployees();
    this.setChangeStatus();
    this.safeContent = this.task?.content ? this.sanitizer.bypassSecurityTrustHtml(this.task?.content) : null;
    this.safeContentInternalNote = this.task?.internalNote
      ? this.sanitizer.bypassSecurityTrustHtml(this.task?.internalNote)
      : null;

    this.form
      .get(TASK_SIDENAV_FORM.settlementStatus)
      .setValue(task?.settlementStatus?.toString() || ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_NONE, {
        onlySelf: true
      });
  }

  setChangeStatus() {
    let status = this.task?.changeStatus;
    if (this.task?.changeStatus === ProjectChangesStatus.CHANGE_STATUS_EDITED) {
      status = ProjectChangesStatus.CHANGE_STATUS_CREATED;
    }
    this.form.get(TASK_SIDENAV_FORM.changeStatus).setValue(status, { onlySelf: true });
    this.changeStatusHelper.setValue(status, { onlySelf: true });
  }

  translate(prefix, value: string) {
    const translation = this.t.instant(`${prefix}${value}`);
    if (translation !== `${prefix}${value}`) {
      return translation;
    }
    return value;
  }

  setGroupID(id: number, onlySelf: boolean = true) {
    if (!id) return;

    this.defaultGroupID = id || null;
    this.form
      .get(TASK_SIDENAV_FORM.groupId)
      .patchValue(this.defaultGroupID, onlySelf ? { onlySelf: true } : {});
  }

  setAssigneeEmails(emails: string[]) {
    if (emails?.length) {
      this.assignedUsers = [...emails.map((e) => this.mockList.createCustomTag(e))];
      this.form.get(TASK_SIDENAV_FORM.assignedTo).setValue(this.assignedUsers.map((i) => i.id));
    }
  }

  setAssigneeIds(ids: number[]) {
    if (ids?.length) {
      this.assignedUsers = [...ids];
      this.form.get(TASK_SIDENAV_FORM.assignedTo).setValue(ids);
    }
  }

  setGroupType(type: 'project' | 'without_project' | 'activity-template') {
    this.groupType = type || this.groupType;
  }

  defaultFormFill() {
    const ignoreStates = [
      TASK_SIDENAV_FORM.id,
      TASK_SIDENAV_FORM.protocolId,
      TASK_SIDENAV_FORM.projectId
    ].map((i) => i.toString());
    // Don`t put id to Form, because that is for project
    Object.keys(this.task).forEach((key: string) => {
      if (!(ignoreStates.indexOf(key) > -1)) {
        const control = this.form.get(key);
        !!control ? control.setValue(this.task[key]) : null;
      }
    });
  }

  subscribePreviewChange() {
    const shouldAutosave =
      !this.task?.isDeleted &&
      this.permissionCtrl.edit &&
      (this.isPreview || (this.isEdit && !this.hasProcessTaskContext));

    if (!shouldAutosave) return;

    const $source = this.form.valueChanges.pipe(
      distinctUntilChanged((a, b) => this.compareFormValues(a, b)),
      filter(() => (this.isPreview || (this.isEdit && !this.hasProcessTaskContext)) && this.isOpened),
      concatMap(() =>
        this.saveForm({
          editMode: this.hasProcessTaskContext ? 'PROJECT' : undefined
        })
      )
    );

    this.previewFormSub = $source.subscribe();
  }

  subscribeUpdateTask() {
    if (this.task?.isDeleted) return;

    const actions = [ETaskAction.ACTION_SHARE];
    const actionsSub = this.taskActionEmitter
      .pipe(filter((i) => actions.indexOf(i.action) > -1))
      ?.subscribe((i: ITaskActionEvent) => this.reloadTask());

    const protocolsSub = this.taskService.updateTaskList.subscribe(() => this.reloadTask());

    this.sub.add(actionsSub);
    this.sub.add(protocolsSub);
  }

  reloadTask() {
    if (!this.isOpened) return;
    this.getTaskRequest(this.task?.id)
      .subscribe((task: Task) => this.initPreviewState(task))
      .add(() => (this.loadingTask = false));
  }

  saveForm({ isWorkspace, editMode }: { data?: any; isWorkspace?: boolean; editMode?: 'PROJECT' | 'COMPANY' }) {
    this.loading = true;
    const payload = this.getFormData({ isWorkspace });
    if (editMode) {
      payload.editMode = editMode;
    }

    return this.taskService.editTask(payload, this.task.id).pipe(
      switchMap((task: Task) => {
        const { assignedEmails, assignedToIds } = payload;

        if (isWorkspace && (assignedToIds.length || assignedEmails.length)) {
          const immutableGroupId = Array.from(this.listTaskService.listGroupTasksHashMap.values()).find(
            (group) => group.taskGroup?.immutable
          )?.taskGroup.id;
          const groupId = this.form.value.groupId || immutableGroupId;
          return this.taskService
            .assignTaskToGroup(task.id, groupId)
            .pipe(mapTo({ ...task, workspaceTaskGroup: { id: groupId } }));
        }

        return of(task);
      }),
      tap((task: Task) => {
        this.initPreviewState(task);
        this.taskActionEmitter.emit({ action: ETaskAction.ACTION_UPDATE, data: { task } });
        this.listTaskService.emitter.emit({
          type: ListTaskEventType.UPDATE_TASK,
          data: { task: new Task(task) }
        });
        this.listTaskService.emitter.emit({
          type: ListTaskEventType.UPDATE_COUNTERS,
          data: { projectId: task?.project?.id, groupType: task?.type }
        });

        if (!this.hasProcessTaskContext && (this.isPreview || this.isEdit)) {
          this.snackBarService.success(this.t.instant(`Tasks.${task.type}Changed`));
        }
      }),
      finalize(() => (this.loading = false))
    );
  }

  setSelectedItems() {
    this.selectedEmployees = this.searchEmployeeRef?.formData || null;
  }

  createForm() {
    this.form = this.fb.group(this.initialFormValues);
    this.setRequiredValidators(TASK_SIDENAV_FORM.description);
  }

  setRequiredValidators(name: string, clear: boolean = false) {
    this.form.get(name).setValidators(clear ? [] : [Validators.required]);
    this.form.get(name).updateValueAndValidity();
  }

  registerAttachmentList(attachmentsListRef: AttachmentManagerListComponent) {
    this.attachmentsListRef = attachmentsListRef;
  }

  unregisterAttachmentList() {
    this.attachmentsListRef = null;
  }

  registerGroupRef(ref: any) {
    this.groupRef = ref;
  }

  unregisterGroupRef() {
    this.groupRef = null;
  }

  registerSearchEmployee(searchEmployeeRef: EmployeeSearchComponent) {
    this.searchEmployeeRef = searchEmployeeRef;
  }

  unregisterSearchEmployee() {
    this.searchEmployeeRef = null;
  }

  registerChangeTemplates(options) {
    this.financeTemplate = options.financeTemplate;
    this.daysTemplate = options.daysTemplate;
  }

  unregisterChangeTemplates() {
    this.financeTemplate = null;
    this.daysTemplate = null;
  }

  rangeChanged($event) {
    const startControl = this.form.get(TASK_SIDENAV_FORM.termStart);
    const endControl = this.form.get(TASK_SIDENAV_FORM.termEnd);

    startControl.setValue(
      !!$event.start ? moment($event.start, Config.DATE_SERVER).format(Config.DATE_SERVER) : null,
      { onlySelf: true, emitEvent: false }
    );

    endControl.setValue(
      !!$event.end ? moment($event.end, Config.DATE_SERVER).format(Config.DATE_SERVER) : null,
      { onlySelf: true, emitEvent: false }
    );

    this.selectedDate = {
      start: startControl.value
        ? moment(startControl.value, Config.DATE_SERVER).format(Config.DATE_FORMAT_DOTS)
        : null,
      end: endControl.value
        ? moment(endControl.value, Config.DATE_SERVER).format(Config.DATE_FORMAT_DOTS)
        : null
    };
    startControl.setValue(startControl.value);
  }

  closeTaskSidenav() {
    this.isOpened = false;
    this.sideService.close();
    this.hardResetSidenav();
  }

  resetTaskSidenav() {
    this.taskSubscription?.unsubscribe();
    this.subUpdateTask?.unsubscribe();
    this.previewFormSub?.unsubscribe();
    this.form.setValue(this.initialFormValues);
    Object.keys(this.TASK_SIDENAV_FORM).forEach((key: string) => {
      this.form.get(key)?.markAsUntouched();
      this.form.get(key)?.updateValueAndValidity({ onlySelf: true });
    });
    this.groupType = null;
  }

  hardResetSidenav() {
    this.showInCalendar = false;
    this.resetTaskSidenav();
    this.resetDefaultValuesWithinSidenav();
    this.taskActionEmitter.emit({ action: ETaskAction.ACTION_DISCARD, data: {} });
  }

  resetDefaultValuesWithinSidenav() {
    this.unregisterChangeTemplates();
    this.unregisterAttachmentList();
    this.unregisterSearchEmployee();
    this.unregisterGroupRef();

    if (!this.nextTaskCreation) {
      this.defaultGroupID = null;
      this.projectID = null;
    }
    this.connectedTask = null;
    this.task = null;
    this.taskId = null;
    this.selectedDate = null;
    this.selectedEmployees = null;
    this.assignedUsers = [];
    this.activityTemplateContext = null;
    this.activityTemplateTaskType = null;
    this.isProcessTaskContext = false;
    this.permissionCtrl.initPermissions(this.task);

    this.subUpdateTask?.unsubscribe();
    this.statusSubscription?.unsubscribe();
    this.previewFormSub?.unsubscribe();

    this.nextTaskCreation = false;
  }

  setTaskGroup(i: { id: number; text: string }) {
    this.selectedGroup = i;
    this.setGroupID(i?.id, false);
  }

  setTaskType(i: { type: string; name: string }) {
    this.selectedType = i;
    this.form.get(TASK_SIDENAV_FORM.type).setValue(i?.type);
  }

  setNewTask(task: Task) {
    this.task = this.createTask(task);
    this.projectID = task?.project?.id || null;
    this.assignUsers();
    this.permissionCtrl.initPermissions(this.task);
    this.form.get(TASK_SIDENAV_FORM.projectId).patchValue(this.projectID, { onlySelf: true });
  }

  createTask(_task: Task): Task {
    const task = new Task(_task);
    task.project ? (task.project = new Project(task.project)) : null;
    task.setChildrenFirstLevel();
    return task;
  }

  assignUsers() {
    const emailCustomTags = this.task?.assignedEmails?.map((i) => this.mockList?.createCustomTag(i)) || [];
    const userCustomTags =
      this.task?.assignedUsers?.map((i) => this.mockList?.createTagFromUserPerson(i)) || [];

    this.assignedUsers = [...userCustomTags.concat(emailCustomTags)];
  }

  createCustomTag(name: string): any {
    return { email: name, previewName: name, helperID: name, newItem: true, label: name };
  }

  discardSaving(): void {
    const nothingChanged =
      (this.isCreation || this.isEdit) &&
      this.compareFormValues(this.savedFormValues, this.currentFormValues) &&
      !this.attachmentsListRef?.service?.attachmentPacket?.id;

    if (this.isPreview || nothingChanged) {
      this.closeTaskSidenav();
      return;
    }

    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.DEFAULT_MODAL_WIDTH,
        autoFocus: false,
        data: {
          title: 'LoseData.headerTitle',
          body: 'LoseData.description',
          submitButtonText: 'LoseData.confirm',
          submitButtonType: ButtonTypes.NEW_RED
        }
      })
      .afterClosed()
      .pipe(
        tap((confirmed: boolean | undefined) => {
          if (confirmed) this.closeTaskSidenav();
        })
      )
      .subscribe();
  }

  getFormData({ obj, isWorkspace }: { obj?: any; isWorkspace?: boolean }) {
    const projectIDCtrl = this.form.get(TASK_SIDENAV_FORM.projectId);
    const model = obj ? Object.assign(obj, this.form.value) : Object.assign({}, this.form.value);

    if (isWorkspace) {
      delete model.groupId;
    } else {
      model.groupId = projectIDCtrl?.value ? model.groupId : null;
    }

    model.attachmentPacketId = this?.attachmentsListRef?.service?.attachmentPacket?.id || null;
    model.attachments = [];

    model.objectType = ObjectTaskType.PROJECT;

    if (this.isTaskCompanyClient) {
      model.objectType = ObjectTaskType.PROJECT_BOX_1;
    }

    if (this.isConfigChange && this.isProtocol) {
      model.id = model.protocolId;
      model.objectType = ObjectTaskType.PROTOCOL;
    } else {
      model.id = model.projectId;
    }

    model.objectType = projectIDCtrl?.value ? model.objectType : ObjectTaskType.PLAIN;
    delete model.assignedToIds;
    delete model.protocol;

    this.setSelectedItems();
    const data = Object.assign(model, this.selectedEmployees);

    const withoutResend = () => {
      const o = Object.assign({}, this.savedFormValues);
      const n = Object.assign({}, this.currentFormValues);

      const priorityChanged = o?.isHighPriority !== n?.isHighPriority;
      delete o?.isHighPriority;
      delete n?.isHighPriority;

      const hiddenForClientChanged = o?.hiddenForClient !== n?.hiddenForClient;
      delete o?.hiddenForClient;
      delete n?.hiddenForClient;

      const internalNoteChanged = o?.internalNote !== n?.internalNote;
      delete o?.internalNote;
      delete n?.internalNote;

      const settlementStatusChanged = o?.settlementStatus !== n?.settlementStatus;
      delete o?.settlementStatus;
      delete n?.settlementStatus;

      return (
        (priorityChanged || hiddenForClientChanged || internalNoteChanged || settlementStatusChanged) &&
        this.compareFormValues(o, n)
      );
    };

    const _withoutResend = withoutResend();

    data.dataChanges = this.isStatusChanged || _withoutResend ? 0 : 1;
    return data;
  }

  addNewTask(isWorkspace?: boolean): Observable<Task> {
    if (this.loading) return EMPTY;

    this.loading = true;

    let payload = this.getFormData({ isWorkspace });

    return this.subtasksService.creationSubtasksList$.pipe(
      take(1),
      tap((value) => {
        payload = {
          ...payload,
          childs: value.map((t: any) => ({
            description: t.description,
            assignedToIds: t.assignedToIds,
            assignedEmails: t.assignedEmails,
            type: payload.type || this.TaskConfigType,
            ...(t.termStart ? { termStart: t.termStart } : {}),
            ...(t.termEnd ? { termEnd: t.termEnd } : {}),
            ...(t.linkedChangeId ? { linkedChangeId: t.linkedChangeId } : {}),
            ...(t.linkedChange ? { linkedChange: t.linkedChange } : {})
          }))
        };
      }),
      switchMap(() =>
        this.createTaskRequest(payload).pipe(
          switchMap((task: Task) => {
            const currentUserEmployeeId = this.storage.UserPerson?.employees?.[0]?.id;
            const assignedToIds = this.form.getRawValue().assignedToIds;

            if (
              isWorkspace &&
              this.form.value.groupId &&
              Array.isArray(assignedToIds) &&
              assignedToIds.includes(currentUserEmployeeId)
            ) {
              return this.taskService
                .assignTaskToGroup(task.id, this.form.value.groupId)
                .pipe(mapTo({ ...task, workspaceTaskGroup: { id: this.form.value.groupId } }));
            }

            return of(task);
          })
        )
      ),
      tap((task: Task) => {
        this.setConnectedTask(task?.linkedChange);
        this.successAddingTask(task);
      }),
      finalize(() => (this.loading = false))
    );
  }

  private createTaskRequest(payload: any): Observable<Task> {
    if (!this.activityTemplateContext?.stage) {
      return this.taskService.createTask(payload);
    }

    const taskPayload = {
      description: payload?.description || '',
      type: this.activityTemplateTaskType || payload?.type || this.TaskConfigType,
      content: payload?.content || null,
      quillContent: payload?.quillContent || null,
      price: Number(payload?.price || 0),
      isPriceEstimated: !!payload?.isPriceEstimated,
      discount: Number(payload?.discount || 0),
      additionalTime: Number(payload?.additionalTime || 0),
      isAdditionalTimeEstimated: !!payload?.isAdditionalTimeEstimated,
      hiddenForClient: payload?.hiddenForClient !== false,
      isHighPriority: !!payload?.isHighPriority,
      internalNote: payload?.internalNote || '',
      emailTemplateId: payload?.emailTemplateId || null,
      tags: Array.isArray(payload?.tags) ? payload.tags : [],
      assignedEmails: Array.isArray(payload?.assignedEmails) ? payload.assignedEmails : [],
      assignedToIds: Array.isArray(payload?.assignedToIds) ? payload.assignedToIds : [],
      childs: Array.isArray(payload?.childs) ? payload.childs : []
    };

    const activityPayload = {
      task: taskPayload,
      stage: this.activityTemplateContext.stage,
      sortOrder: this.activityTemplateContext.sortOrder ?? 1,
      parentId: this.activityTemplateContext.parentId ?? null
    };

    return this.taskService.createActivityTemplateTask(activityPayload).pipe(
      tap((task: Task) => {
        (task as Task & { _isActivityTemplate?: boolean })._isActivityTemplate = true;
      })
    );
  }

  successAddingTask(task: Task) {
    this.resetTaskSidenav();
    this.taskActionEmitter.emit({ action: ETaskAction.ACTION_CREATE, data: { task } });
    console.log('successAddingTask');
    this.listTaskService.emitter.emit({ type: ListTaskEventType.ADD_TASK, data: task });
  }

  deleteTask() {
    return this.taskService.deleteTask(this.task.id).pipe(
      tap((i) => {
        this.updateTaskOnRemove();
        this.taskActionEmitter.emit({ action: ETaskAction.ACTION_DELETE, data: { task: this.task } });
      })
    );
  }

  updateTaskOnRemove() {
    this.task.deleted = moment().format(Config.DATE_SERVER);
  }
}
